/**
 * Full-screen style dialog for user edit — matches Angular admin opening
 * `UserManagementEditComponent` in a modal from transfer / similar flows.
 */
import { useEffect } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock"
import { UserManagementEditPage } from "../pages/UserManagementEditPage"
import modalStyles from "@/features/application/styles/modals.module.css"
import type { UserManagementEditEmbeddedModalProps } from '../types'

export function UserManagementEditEmbeddedModal({
  isOpen,
  userId,
  onClose,
  title = "Edit user",
  overlayDataTestId = "um-edit-embedded-modal-overlay",
  panelDataTestId = "um-edit-embedded-modal-panel",
}: UserManagementEditEmbeddedModalProps) {
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, onClose])

  if (!isOpen || userId == null) return null

  const uid = String(userId)

  return createPortal(
    <div
      className="fixed inset-0 z-[10002] flex items-stretch justify-center sm:p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="um-edit-embedded-title"
      data-testid={overlayDataTestId}
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="relative z-10 flex h-full w-full max-w-[min(100%,1280px)] flex-col overflow-hidden rounded-none border border-slate-200 bg-[#f8fafc] shadow-2xl sm:my-auto sm:max-h-[min(92vh,1200px)] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid={panelDataTestId}
      >
        <div
          className={`${modalStyles.modalValidationHeader} ${modalStyles.modalShellHeaderChrome} shrink-0`}
        >
          <h3
            id="um-edit-embedded-title"
            className={modalStyles.modalValidationTitle}
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
            aria-label="Close"
            data-testid="um-edit-embedded-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
          <UserManagementEditPage
            embedded
            embeddedUserId={uid}
            onEmbeddedClose={onClose}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
